import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckIcon, MinusIcon } from 'lucide-react';
import { useAssessment } from '../contexts/AssessmentContext';
const CAPTURED = ['Which documents you open, and when', 'Flags, corrections, and everything you write in them', 'Every prompt to the assistant, its response, and anything you paste in', 'Timestamps for the actions above', 'Your final submission and reflection'];
const NOT_CAPTURED = ['Webcam or video of any kind', 'Keystrokes outside the input fields on this page', 'Clipboard contents from outside this window', 'Attention, gaze, or focus tracking', 'Anything at all after the session ends'];
export function Consent() {
  const {
    giveConsent,
    participantId: existingId
  } = useAssessment();
  const [participantId, setParticipantId] = useState(existingId);
  const [agreed, setAgreed] = useState(false);
  const navigate = useNavigate();
  const canContinue = participantId.trim().length >= 3 && agreed;
  return <main className="w-full flex-1 bg-slate-100 py-12" data-id="element-424" data-mp="1aj5jod64tzdr">
      <div className="mx-auto max-w-3xl px-6" data-id="element-425" data-mp="1fh7bbmhmj70u">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-426" data-mp="1ffvyyya44qy4">
          Before we begin
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900" data-id="element-427" data-mp="10q6v2vamy8u5">
          What this session records, and what it does not
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-slate-600" data-id="element-428" data-mp="rrcyh4xb1igl">
          This is a research session. Please read this page in full — you can stop at any point, and
          you can ask for your record to be deleted afterwards.
        </p>

        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6" data-id="element-429" data-mp="hvjdcx5isgn7">
          <label htmlFor="pid" className="block text-sm font-medium text-slate-900" data-id="element-430" data-mp="mss07b1m58us">
            Participant ID
          </label>
          <p className="mt-1 text-xs text-slate-500" data-id="element-431" data-mp="2h6j5xzgtn8ky">
            Use the code from your invitation. Your name stays out of the assessment record by design.
          </p>
          <input id="pid" value={participantId} onChange={e => setParticipantId(e.target.value.toUpperCase())} placeholder="e.g. P-014" className="mt-3 w-56 rounded-md border border-slate-300 px-3 py-2 font-mono-num text-sm uppercase tracking-wide text-slate-900 placeholder:normal-case placeholder:tracking-normal placeholder:text-slate-400 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200" data-id="element-432" data-mp="2fqovunm90puz" />
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2" data-id="element-433" data-mp="2tivcsbx870vh">
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-434" data-mp="2op0jcgrtj7j7">
            <h2 className="text-sm font-semibold text-slate-900" data-id="element-435" data-mp="2jr8vpyy7i7kt">Captured</h2>
            <ul className="mt-3 space-y-2.5" data-id="element-436" data-mp="2jp4ob713mqfs">
              {CAPTURED.map(c => <li key={c} className="flex items-start gap-2.5 text-[13.5px] leading-snug text-slate-700" data-id="element-437" data-mp="3h8fy31guqa7x">
                  <CheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-teal-600" data-id="element-438" data-mp="3cdthmjvwxrgz" />
                  {c}
                </li>)}
            </ul>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-439" data-mp="3oqdzeiovqf02">
            <h2 className="text-sm font-semibold text-slate-900" data-id="element-440" data-mp="3tnxe1jphotsj">Not captured</h2>
            <ul className="mt-3 space-y-2.5" data-id="element-441" data-mp="2ixoyao2w73e">
              {NOT_CAPTURED.map(c => <li key={c} className="flex items-start gap-2.5 text-[13.5px] leading-snug text-slate-700" data-id="element-442" data-mp="7iab0t74la4x">
                  <MinusIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" data-id="element-443" data-mp="bikfu2j6lui7" />
                  {c}
                </li>)}
            </ul>
          </div>
        </div>

        <div className="mt-6 rounded-xl border-l-4 border-slate-900 bg-white p-6" data-id="element-444" data-mp="l9otmyx4f9bg">
          <p className="text-[15px] font-medium leading-relaxed text-slate-900" data-id="element-445" data-mp="q88jvbiqz79m">
            A named human reviews the full record before any conclusion is drawn. Nothing is decided
            by the system.
          </p>
        </div>

        <div className="mt-6 space-y-4 rounded-xl border border-slate-200 bg-white p-6 text-[13.5px] leading-relaxed text-slate-600" data-id="element-446" data-mp="1rxomkxu9bgyn">
          <p data-id="element-447" data-mp="1mxj15zekyqp0">
            <span className="font-semibold text-slate-900" data-id="element-448" data-mp="1mxsqdkrswttn">Retention and withdrawal. </span>
            Your record is held for twelve months, linked only to your participant ID. You can
            withdraw it at any time before or after the session by quoting that ID — no reason
            needed, and no consequence.
          </p>
          <p data-id="element-449" data-mp="141kkqfuvmgxt">
            <span className="font-semibold text-slate-900" data-id="element-450" data-mp="141suhyvpd64s">Accommodations. </span>
            Extended timing and reduced-telemetry modes are available — contact us before starting.
          </p>
        </div>

        <label className="mt-6 flex cursor-pointer items-start gap-3 rounded-xl border border-slate-300 bg-white p-5" data-id="element-451" data-mp="1dy5mmsblnbxf">
          <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400" data-id="element-452" data-mp="2tdw7rivdqm8r" />
          <span className="text-sm text-slate-800" data-id="element-453" data-mp="31x98amg5pk27">
            I have read the above and consent to taking part in this session on these terms
          </span>
        </label>

        <button type="button" disabled={!canContinue} onClick={() => {
        giveConsent(participantId);
        navigate('/briefing');
      }} className="mt-6 rounded-lg bg-slate-900 px-6 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300" data-id="element-454" data-mp="2ryk05le2bdz5">
          Continue
        </button>
      </div>
    </main>;
}